import React, { useState, useRef, useEffect, useCallback } from 'react';
import { checkMergeStatus, cancelMergeProcessing } from '../../../services/api';
import { buildApiUrl, API_ENDPOINTS } from '../../../config/api';
import { getCsrfHeaders } from '../../../utils/csrf';
import { useAuth } from '../../../context/AuthContext';
import { authFetch } from '../../../utils/authFetch';
import '../UploadForm/loading.css';
import DownloadStatus from '../../common/DownloadStatus';
import FeedbackSection from '../../feedback/FeedbackSection';
import RegistrationCTA from '../../common/RegistrationCTA/RegistrationCTA';

// Maximum file size per video (200MB)
const MAX_VIDEO_SIZE = 200 * 1024 * 1024;

// Maximum total size for all videos (500MB)
const MAX_TOTAL_SIZE = 500 * 1024 * 1024;

// Supported video formats
const SUPPORTED_FORMATS = ['.mp4', '.avi', '.mov', '.mkv', '.webm'];

// File name validation regex
const VALID_FILENAME_PATTERN = /^[a-zA-Z0-9_\-.\s[\](),']+$/;

// Regex to extract numeric suffix (001, 002, etc.)
const NUMERIC_SUFFIX_PATTERN = /(\d{3})(?=\.[^.]+$)/;

const MAX_VIDEO_DURATION_SECONDS = 600; // 10 minutes per video

// Function to extract numeric suffix from filename
const extractNumericSuffix = (filename) => {
  const match = filename.match(NUMERIC_SUFFIX_PATTERN);
  if (match) {
    return parseInt(match[1], 10);
  }
  // Fallback: look for any number at the end
  const fallbackMatch = filename.match(/(\d+)(?=\.[^.]+$)/);
  if (fallbackMatch) {
    return parseInt(fallbackMatch[1], 10);
  }
  return -1; // No number found
};

const VideoMerge = () => {
  const { getAuthHeaders } = useAuth();
  const [videos, setVideos] = useState([]);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [overallProgress, setOverallProgress] = useState(0);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  // Fetch usage info on component mount
  useEffect(() => {
    const fetchUsageInfo = async () => {
      try {
        const response = await authFetch(buildApiUrl(API_ENDPOINTS.USAGE()), {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const usage = await response.json();
          setUsageInfo(usage);
        }
      } catch (error) {
        console.error('Usage check error:', error);
      }
    };
    fetchUsageInfo();
  }, [getAuthHeaders]);

  // Status polling
  const pollMergeStatus = useCallback((sessionId) => {
    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await checkMergeStatus(sessionId);
        const status = response.data;
        
        setProcessingStatus(status.status);
        
        // Update localStorage
        const savedState = localStorage.getItem('mergeState');
        if (savedState) {
          const state = JSON.parse(savedState);
          state.status = status.status;
          localStorage.setItem('mergeState', JSON.stringify(state));
        }
        
        if (status.status === 'completed') {
          setIsProcessing(false);
          setUploadCompleted(true);
          setMessage({
            text: 'Video merge completed successfully! You can now download your merged video.',
            type: 'success'
          });
          // Clear both interval and timeout
          clearInterval(pollIntervalRef.current);
          clearTimeout(pollTimeoutRef.current);
          pollIntervalRef.current = null;
          pollTimeoutRef.current = null;
        } else if (status.status === 'failed') {
          setIsProcessing(false);
          setMessage({
            text: `Merge failed: ${status.error_message || 'Unknown error'}`,
            type: 'error'
          });
          clearInterval(pollIntervalRef.current);
          clearTimeout(pollTimeoutRef.current);
          pollIntervalRef.current = null;
          pollTimeoutRef.current = null;
        } else if (status.status === 'cancelled') {
          setIsProcessing(false);
          setMessage({
            text: 'Video merge was cancelled',
            type: 'info'
          });
          clearInterval(pollIntervalRef.current);
          clearTimeout(pollTimeoutRef.current);
          pollIntervalRef.current = null;
          pollTimeoutRef.current = null;
        }
      } catch (error) {
        console.error('Status check error:', error);
      }
    }, 2000);
    
    // Cleanup after 60 minutes (3600 seconds)
    // Video merge can take longer for large/multiple files
    pollTimeoutRef.current = setTimeout(() => {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      pollTimeoutRef.current = null;
      setIsProcessing(false);
      setMessage({
        text: 'Processing timeout after 60 minutes. The video may still be processing on the server. You can refresh the page to check the status.',
        type: 'error'
      });
    }, 3600000);
  }, []);

  // Check for existing job on mount
  useEffect(() => {
    const savedState = localStorage.getItem('mergeState');
    if (savedState) {
      const state = JSON.parse(savedState);
      setSessionId(state.sessionId);
      setProcessingStatus(state.status);
      
      // Create dummy videos array for UI rendering (needed to show buttons)
      if (state.videoCount) {
        const dummyVideos = state.videoNames && state.videoNames.length > 0
          ? state.videoNames.map((video, i) => ({
              id: `dummy-${i}`,
              name: video.name,
              size: 0,
              numericSuffix: video.numericSuffix
            }))
          : Array.from({ length: state.videoCount }, (_, i) => ({
              id: `dummy-${i}`,
              name: `Video ${i + 1}`,
              size: 0,
              numericSuffix: i + 1
            }));
        setVideos(dummyVideos);
      }
      
      if (state.status === 'pending' || state.status === 'processing') {
        setIsProcessing(true);
        pollMergeStatus(state.sessionId);
      } else if (state.status === 'completed') {
        setUploadCompleted(true);
        setIsProcessing(false);
        setMessage({
          text: 'Video merge completed! You can download your merged video.',
          type: 'success'
        });
      } else if (state.status === 'failed') {
        setIsProcessing(false);
        setMessage({
          text: 'Video merge failed.',
          type: 'error'
        });
      }
    }
  }, [pollMergeStatus]);

  // Handle cancel
  const handleCancelProcessing = async () => {
    if (isLoading && !sessionId) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsLoading(false);
      setMessage({
        text: 'Upload cancelled successfully',
        type: 'info'
      });
      return;
    }

    if (!sessionId) {
      setMessage({
        text: 'No active processing to cancel',
        type: 'error'
      });
      return;
    }

    try {
      await cancelMergeProcessing(sessionId);
      setIsProcessing(false);
      setIsLoading(false);
      setMessage({
        text: 'Video merge cancelled successfully',
        type: 'success'
      });
      
      // Update localStorage
      const savedState = localStorage.getItem('mergeState');
      if (savedState) {
        const state = JSON.parse(savedState);
        state.status = 'cancelled';
        localStorage.setItem('mergeState', JSON.stringify(state));
      }
    } catch (error) {
      console.error('Error cancelling processing:', error);
      setMessage({
        text: 'Failed to cancel processing. Please try again.',
        type: 'error'
      });
    }
  };

  // Validate and add video file
  const validateAndAddVideo = async (file) => {
    // Validate file size
    if (file.size > MAX_VIDEO_SIZE) {
      return {
        valid: false,
        error: `${file.name}: File too large (max 200MB)`
      };
    }

    // Validate format
    const ext = file.name.toLowerCase().split('.').pop();
    if (!SUPPORTED_FORMATS.includes(`.${ext}`)) {
      return {
        valid: false,
        error: `${file.name}: Unsupported format`
      };
    }

    // Validate filename
    if (!VALID_FILENAME_PATTERN.test(file.name)) {
      return {
        valid: false,
        error: `${file.name}: Invalid characters in filename`
      };
    }

    // Extract numeric suffix
    const numericSuffix = extractNumericSuffix(file.name);
    if (numericSuffix === -1) {
      return {
        valid: false,
        error: `${file.name}: Missing numeric suffix (e.g., video001.mp4)`
      };
    }

    // Validate duration
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const videoElement = document.createElement('video');
      videoElement.preload = 'metadata';

      videoElement.onloadedmetadata = () => {
        window.URL.revokeObjectURL(url);
        if (videoElement.duration > MAX_VIDEO_DURATION_SECONDS) {
          resolve({
            valid: false,
            error: `${file.name}: Video too long (max 10 minutes)`
          });
        } else {
          resolve({
            valid: true,
            file: file,
            numericSuffix: numericSuffix
          });
        }
      };

      videoElement.onerror = () => {
        window.URL.revokeObjectURL(url);
        resolve({
          valid: false,
          error: `${file.name}: Could not read video file`
        });
      };

      videoElement.src = url;
    });
  };

  // Handle file selection
  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    await processFiles(files);
  };

  // Process multiple files
  const processFiles = async (files) => {
    if (files.length === 0) return;

    setMessage(null);
    const newVideos = [];
    const errors = [];

    for (const file of files) {
      const result = await validateAndAddVideo(file);
      if (result.valid) {
        newVideos.push({
          file: result.file,
          name: result.file.name,
          size: result.file.size,
          numericSuffix: result.numericSuffix,
          id: `${result.file.name}-${Date.now()}-${Math.random()}`
        });
      } else {
        errors.push(result.error);
      }
    }

    if (errors.length > 0) {
      setMessage({
        text: errors.join('; '),
        type: 'error'
      });
    }

    if (newVideos.length > 0) {
      // Sort by numeric suffix
      newVideos.sort((a, b) => a.numericSuffix - b.numericSuffix);
      setVideos(prev => {
        const combined = [...prev, ...newVideos];
        // Remove duplicates and sort
        const unique = combined.filter((v, i, arr) => 
          arr.findIndex(t => t.name === v.name) === i
        );
        return unique.sort((a, b) => a.numericSuffix - b.numericSuffix);
      });
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading || isProcessing || uploadCompleted) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (isLoading || isProcessing || uploadCompleted) return;

    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  };

  // Remove video from list
  const removeVideo = (videoId) => {
    setVideos(prev => prev.filter(v => v.id !== videoId));
  };

  // Reset everything and start fresh
  const handleUploadNewVideos = async () => {
    // If there's an active processing job on backend, cancel it first
    if (sessionId && (isProcessing || isLoading)) {
      try {
        await cancelMergeProcessing(sessionId);
      } catch (error) {
        console.error('Error cancelling backend processing:', error);
        // Continue with cleanup even if cancel fails
      }
    }
    
    // Cancel any ongoing upload operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear polling intervals and timeouts
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    
    // Clear localStorage
    localStorage.removeItem('mergeState');
    
    // Reset all state
    setVideos([]);
    setMessage(null);
    setUploadCompleted(false);
    setIsLoading(false);
    setIsProcessing(false);
    setProcessingStatus(null);
    setSessionId(null);
    setUploadProgress({});
    setOverallProgress(0);
    setDownloadStatus(null);
    setDownloadMessage("");
    setIsDragOver(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle upload with direct R2 upload
  const handleUpload = async () => {
    if (videos.length < 2) {
      setMessage({
        text: 'Please add at least 2 videos to merge',
        type: 'error'
      });
      return;
    }

    // Calculate total size
    const totalSize = videos.reduce((sum, v) => sum + v.size, 0);
    
    // Validate total size
    if (totalSize > MAX_TOTAL_SIZE) {
      setMessage({
        text: `Total size of all videos (${(totalSize / (1024*1024)).toFixed(1)} MB) exceeds maximum limit of ${MAX_TOTAL_SIZE / (1024*1024)} MB`,
        type: 'error'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);
    setUploadProgress({});
    setOverallProgress(0);

    abortControllerRef.current = new AbortController();

    try {
      // Step 1: Initiate merge upload - get presigned URLs for all files
      setMessage({
        text: 'Preparing upload...',
        type: 'info'
      });

      const filesMetadata = videos.map(v => ({
        filename: v.name,
        content_type: v.file.type || 'video/mp4',
        file_size: v.size
      }));

      const initiateResponse = await authFetch(buildApiUrl('/merge/upload/initiate/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCsrfHeaders(),
        },
        body: JSON.stringify({
          files: filesMetadata
        }),
        signal: abortControllerRef.current.signal
      });

      if (!initiateResponse.ok) {
        const errorData = await initiateResponse.json();
        throw new Error(errorData.error || 'Failed to initiate merge upload');
      }

      const initiateData = await initiateResponse.json();
      const { upload_urls, session_id } = initiateData;

      // Save session immediately
      setSessionId(session_id);
      localStorage.setItem('mergeState', JSON.stringify({
        sessionId: session_id,
        status: 'uploading',
        videoCount: videos.length,
        videoNames: videos.map(v => ({
          name: v.name,
          numericSuffix: v.numericSuffix
        }))
      }));

      // Step 2: Upload each video directly to R2 with progress tracking
      setMessage({
        text: `Uploading ${videos.length} videos...`,
        type: 'info'
      });

      const uploadPromises = upload_urls.map((urlData, index) => {
        const video = videos.find(v => v.name === urlData.filename);
        if (!video) {
          throw new Error(`Video not found for ${urlData.filename}`);
        }

        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          // Track upload progress for this specific file
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              
              setUploadProgress(prev => {
                const updated = { ...prev, [urlData.filename]: percentComplete };
                
                // Calculate overall progress
                const totalProgress = Object.values(updated).reduce((sum, p) => sum + p, 0);
                const avgProgress = Math.round(totalProgress / videos.length);
                setOverallProgress(avgProgress);
                
                return updated;
              });
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(urlData.object_key);
            } else {
              reject(new Error(`Upload failed for ${urlData.filename} with status ${xhr.status}`));
            }
          });

          xhr.addEventListener('error', () => {
            reject(new Error(`Network error during upload of ${urlData.filename}`));
          });

          xhr.addEventListener('abort', () => {
            reject(new Error(`Upload aborted for ${urlData.filename}`));
          });

          // Handle abort from AbortController
          if (abortControllerRef.current) {
            abortControllerRef.current.signal.addEventListener('abort', () => {
              xhr.abort();
            });
          }

          xhr.open('PUT', urlData.upload_url);
          xhr.setRequestHeader('Content-Type', video.file.type || 'video/mp4');
          xhr.send(video.file);
        });
      });

      const objectKeys = await Promise.all(uploadPromises);
      setOverallProgress(100);

      // Step 3: Complete merge upload - notify backend
      setMessage({
        text: 'Finalizing upload...',
        type: 'info'
      });

      const completeResponse = await authFetch(buildApiUrl('/merge/upload/complete/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCsrfHeaders(),
        },
        body: JSON.stringify({
          session_id: session_id,
          object_keys: objectKeys
        }),
        signal: abortControllerRef.current.signal
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        throw new Error(errorData.error || 'Failed to complete merge upload');
      }

      const completeData = await completeResponse.json();

      // Update state for processing
      setIsProcessing(true);
      setProcessingStatus('pending');

      // Update localStorage
      localStorage.setItem('mergeState', JSON.stringify({
        sessionId: session_id,
        status: 'pending',
        videoCount: videos.length,
        videoNames: videos.map(v => ({ name: v.name, numericSuffix: v.numericSuffix }))
      }));
      
      // Start polling
      pollMergeStatus(session_id);
      
      setMessage({
        text: 'Videos uploaded successfully! Merge processing has started.',
        type: 'success'
      });

      // Update usage info
      if (completeData.usage_info) {
        setUsageInfo(prev => ({
          ...prev,
          ...completeData.usage_info
        }));
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        setMessage({
          text: 'Upload cancelled',
          type: 'info'
        });
        return;
      }

      console.error('Upload error:', error);
      setMessage({
        text: error.message || 'Error uploading videos.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setUploadProgress({});
      setOverallProgress(0);
      abortControllerRef.current = null;
    }
  };

  // Download merged video
  const downloadMergedVideo = async () => {
    if (!sessionId) {
      setDownloadStatus('error');
      setDownloadMessage('Video not available for download');
      return;
    }

    try {
      const downloadEndpoint = buildApiUrl(API_ENDPOINTS.MERGE_DOWNLOAD(sessionId));
      const response = await authFetch(downloadEndpoint);
      
      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }
      
      const data = await response.json();
      window.location.href = data.download_url;
      
      setDownloadStatus('success');
      setDownloadMessage('Download started!');
      
      setTimeout(() => {
        setDownloadStatus(null);
        setDownloadMessage("");
      }, 3000);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('error');
      setDownloadMessage(error.message || 'Error occurred during download');
    }
  };

  // Reset form
  const resetForm = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear localStorage
    localStorage.removeItem('mergeState');
    
    setVideos([]);
    setMessage(null);
    setUploadCompleted(false);
    setIsLoading(false);
    setIsProcessing(false);
    setProcessingStatus(null);
    setSessionId(null);
    setDownloadStatus(null);
    setDownloadMessage(null);
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full">
      <div className="max-w-4xl mx-auto bg-slate-700 bg-opacity-30 rounded-xl p-8">
        <div className="space-y-6">
          <div className="flex flex-col items-center">
            <h2 className="text-2xl font-bold text-slate-100 mb-2">Video Merge</h2>
            <p className="text-slate-400 text-center mb-6 text-sm">
              Upload multiple videos with numeric suffixes (e.g., video001.mp4, video002.mp4) to merge them into a single video
            </p>

            {/* Drag and Drop Zone */}
            <div 
              className={`w-full border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                (isLoading || isProcessing || uploadCompleted)
                  ? 'border-slate-600 bg-slate-900 opacity-50 cursor-not-allowed'
                  : isDragOver 
                    ? 'border-blue-400 bg-blue-500 bg-opacity-5'
                    : 'border-slate-600 hover:border-slate-500'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="fileInput"
                onChange={handleFileChange}
                accept=".mp4,.avi,.mov,.mkv,.webm"
                className="hidden"
                ref={fileInputRef}
                multiple
                disabled={isLoading || isProcessing || uploadCompleted}
              />
              
              <div className="flex flex-col items-center space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="fileInput"
                    className={`inline-block py-2 px-4 rounded-lg transition-colors ${
                      (isLoading || isProcessing || uploadCompleted)
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer'
                    } font-semibold`}
                  >
                    Browse Videos
                  </label>
                  <p className="text-sm text-gray-500">
                    or
                  </p>
                  <p className="text-lg text-gray-300">
                    {isDragOver ? 'Drop your videos here' : 'Drag multiple videos here'}
                  </p>
                </div>
                
                <p className="text-xs text-gray-500">
                  Supported: MP4, AVI, MOV, MKV, WEBM (max 200MB each, 500MB total, 10min each)
                </p>
                <p className="text-xs text-yellow-500">
                  ⚠️ Videos must have numeric suffixes (001, 002, 003, etc.)
                </p>
              </div>
            </div>

            {/* Videos List */}
            {videos.length > 0 && (
              <div className="w-full mt-6">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      Selected Videos ({videos.length})
                    </h3>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-400">
                      Total: {(videos.reduce((sum, v) => sum + v.size, 0) / (1024 * 1024)).toFixed(1)} MB
                      <span className={videos.reduce((sum, v) => sum + v.size, 0) > MAX_TOTAL_SIZE ? ' text-red-400' : ' text-green-400'}>
                        {' '}/ {MAX_TOTAL_SIZE / (1024 * 1024)} MB
                      </span>
                    </div>
                    <button
                      onClick={handleUploadNewVideos}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                      title="Clear all videos and start fresh"
                    >
                      Clear selection
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {videos.map((video, index) => (
                    <div 
                      key={video.id}
                      className="flex items-center justify-between bg-slate-700 rounded-lg p-3 hover:bg-slate-600 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-blue-400 font-bold text-lg">
                          {String(video.numericSuffix).padStart(3, '0')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white truncate">{video.name}</p>
                          {video.size > 0 && (
                            <p className="text-gray-400 text-sm">
                              {(video.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          )}
                        </div>
                      </div>
                      {!isLoading && !isProcessing && !uploadCompleted && (
                        <button
                          onClick={() => removeVideo(video.id)}
                          className="text-red-400 hover:text-red-300 transition-colors px-3 py-1"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Usage Info */}
            {usageInfo && (
              <div className="mt-3 text-sm text-gray-400 text-center">
                {usageInfo.limits?.total_limit ? (
                  <div className="mb-1">
                    Used {usageInfo.usage?.total_videos || 0}/{usageInfo.limits?.total_limit} total
                  </div>
                ) : (
                  <>
                    <div className="mb-1">
                      Used {usageInfo.usage?.videos_today || 0}/{usageInfo.limits?.daily_limit || 20} past 24 hours
                    </div>
                    <div className="mb-1">
                      {usageInfo.usage?.videos_this_hour || 0}/{usageInfo.limits?.hourly_limit || 5} this hour
                    </div>
                  </>
                )}
                {!usageInfo.can_upload && (
                  <div className="text-red-400 text-xs">
                    ❌ {usageInfo.message}
                  </div>
                )}
              </div>
            )}
            
            {/* Registration CTA for anonymous users */}
            <RegistrationCTA />

            {/* Messages */}
            {message && (
              <div className="mt-4 w-full">
                <div className={`p-4 rounded-lg ${message.type === 'error' ? 'bg-red-600' : message.type === 'info' ? 'bg-blue-600' : 'bg-green-600'} text-white`}>
                  {message.text}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {videos.length > 0 && (
            <div className="flex flex-col items-center">
              {!isLoading && !isProcessing && !uploadCompleted && (
                <button
                  onClick={handleUpload}
                  disabled={videos.length < 2 || (usageInfo && !usageInfo.can_upload)}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Merge Videos ({videos.length})
                </button>
              )}

              {(isLoading || isProcessing) && (
                <div className="flex flex-col items-center w-full">
                  <div className="loading-spinner"></div>
                  <p className="loading-text">
                    {isLoading ? "Uploading videos..." : "Merging videos..."}
                  </p>
                  
                  {/* Overall Upload Progress Bar */}
                  {isLoading && overallProgress > 0 && (
                    <div className="w-full max-w-md mt-4">
                      <div className="flex justify-between text-sm text-gray-400 mb-2">
                        <span>Overall Upload Progress</span>
                        <span>{overallProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div 
                          className="bg-blue-500 h-full transition-all duration-300 ease-out"
                          style={{ width: `${overallProgress}%` }}
                        ></div>
                      </div>
                      
                      {/* Individual file progress (optional, for detailed view) */}
                      {Object.keys(uploadProgress).length > 0 && (
                        <div className="mt-3 space-y-1">
                          {videos.slice(0, 5).map(video => ( // Show max 5 videos to avoid clutter
                            uploadProgress[video.name] !== undefined && (
                              <div key={video.name} className="flex items-center gap-2 text-xs text-gray-400">
                                <span className="truncate flex-1">{video.name}</span>
                                <span>{uploadProgress[video.name]}%</span>
                              </div>
                            )
                          ))}
                          {videos.length > 5 && (
                            <p className="text-xs text-gray-500">... and {videos.length - 5} more</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <button
                    onClick={handleCancelProcessing}
                    className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors mt-4"
                  >
                    {isLoading ? "CANCEL UPLOAD" : "STOP PROCESSING"}
                  </button>
                </div>
              )}

              {isProcessing && (
                <div className="mt-4">
                  <p className="text-gray-400 text-sm">Status: {processingStatus}</p>
                </div>
              )}

              {uploadCompleted && (
                <div className="mt-4 w-full max-w-md">
                  {downloadStatus && (
                    <DownloadStatus
                      status={downloadStatus}
                      message={downloadMessage}
                      onClose={() => {
                        setDownloadStatus(null);
                        setDownloadMessage("");
                      }}
                    />
                  )}
                  <button
                    onClick={downloadMergedVideo}
                    className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors w-full"
                  >
                    Download Merged Video
                  </button>
                  <button
                    onClick={resetForm}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors w-full mt-4"
                  >
                    Merge New Videos
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Feedback Section */}
          <div className="mt-6">
            <FeedbackSection />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoMerge;
